import React, { useState } from "react";
import { useAuth } from "@/auth/AuthContext";
import { Card } from "@/components/ui-kit";
import { Target, PencilSimple, CheckCircle, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/auth/AuthContext";
import { computeMonthlyStats } from "@/lib/sharePerformance";
import { shareGoal } from "@/lib/shareGoal";

export const MonthlyGoalCard = ({ stats, lang }) => {
  const { user, updatePrefs } = useAuth();
  const cs = lang === "cs";
  const goal = user?.monthly_goal || 0;
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(goal || "");
  const [busy, setBusy] = useState(false);

  const monthPnl = stats?.month_pnl || 0;
  const pct = goal > 0 ? Math.min(100, Math.max(0, (monthPnl / goal) * 100)) : 0;
  const achieved = goal > 0 && monthPnl >= goal;
  const monthName = new Date().toLocaleDateString(cs ? "cs-CZ" : "en-US", { month: "long", year: "numeric" });

  const save = async () => {
    const num = parseFloat(val);
    if (!num || num <= 0) { toast.error(cs ? "Zadej kladnou částku" : "Enter a positive amount"); return; }
    setBusy(true);
    try {
      await updatePrefs({ monthly_goal: num });
      setEditing(false);
      toast.success(cs ? "Měsíční cíl uložen" : "Monthly goal saved");
    } catch {
      toast.error(cs ? "Uložení selhalo" : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6" data-testid="monthly-goal-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-zinc-100 flex items-center gap-2">
          <Target weight="bold" size={18} className="text-amber-400" />
          {cs ? "Měsíční cíl" : "Monthly goal"}
        </h3>
        <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">{monthName}</span>
      </div>

      {!goal && !editing ? (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <p className="text-sm text-zinc-400">{cs ? "Nastav si měsíční ziskový cíl a sleduj jeho plnění." : "Set a monthly profit goal and track your progress."}</p>
          <button onClick={() => setEditing(true)} data-testid="monthly-goal-set-btn" className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-md text-sm transition-colors">
            {cs ? "Nastavit cíl" : "Set goal"}
          </button>
        </div>
      ) : editing ? (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
            <input
              type="number" min="1" value={val} onChange={(e) => setVal(e.target.value)}
              data-testid="monthly-goal-input" placeholder="1000"
              className="bg-zinc-900 border border-zinc-800 rounded-md pl-7 pr-3 py-2 text-sm text-zinc-100 w-36 focus:outline-none focus:border-blue-500"
            />
          </div>
          <button onClick={save} disabled={busy} data-testid="monthly-goal-save-btn" className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-md text-sm transition-colors disabled:opacity-50">
            {busy ? "..." : (cs ? "Uložit" : "Save")}
          </button>
          <button onClick={() => { setEditing(false); setVal(goal || ""); }} className="text-sm text-zinc-500 hover:text-zinc-200 px-2 transition-colors">
            {cs ? "Zrušit" : "Cancel"}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div className="font-mono">
              <span className={`text-2xl font-medium ${monthPnl >= 0 ? "text-green-400" : "text-red-400"}`} data-testid="monthly-goal-pnl">${monthPnl.toFixed(2)}</span>
              <span className="text-sm text-zinc-500"> / ${goal.toFixed(0)}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-mono text-zinc-400" data-testid="monthly-goal-pct">{pct.toFixed(0)} %</span>
              <button onClick={() => { setVal(goal); setEditing(true); }} data-testid="monthly-goal-edit-btn" title={cs ? "Upravit cíl" : "Edit goal"} className="text-zinc-500 hover:text-zinc-200 transition-colors p-1">
                <PencilSimple weight="bold" size={15} />
              </button>
            </div>
          </div>
          <div className="h-2.5 bg-zinc-900 rounded-full overflow-hidden" data-testid="monthly-goal-bar">
            <div className={`h-full rounded-full transition-all duration-700 ${achieved ? "bg-green-500" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
          </div>
          {achieved && (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-1.5 text-sm text-green-400" data-testid="monthly-goal-achieved">
                <CheckCircle weight="fill" size={16} /> {cs ? "Cíl splněn! Skvělá práce." : "Goal achieved! Great work."}
              </div>
              <button
                onClick={async () => {
                  setBusy(true);
                  try {
                    const { data: items } = await api.get("/journal");
                    const s = computeMonthlyStats(items);
                    const r = await shareGoal({ userName: user?.full_name, goal, monthPnl, winRate: s.winRate, trades: s.trades, lang });
                    if (r === "downloaded") toast.success(cs ? "Obrázek stažen — sdílej ho na Instagram, Facebook nebo X" : "Image downloaded — share it on Instagram, Facebook or X");
                  } catch (e) {
                    if (e?.name !== "AbortError") toast.error(cs ? "Sdílení selhalo" : "Share failed");
                  } finally {
                    setBusy(false);
                  }
                }}
                disabled={busy}
                data-testid="monthly-goal-share-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-amber-500 hover:bg-amber-400 text-zinc-950 transition-colors disabled:opacity-50"
              >
                <ShareNetwork weight="bold" size={14} /> {cs ? "Sdílet úspěch" : "Share achievement"}
              </button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
